import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { X } from 'lucide-react';

import { Button } from '@/component-library/primitives/Button/Button';
import { Input } from '@/component-library/widgets/Input/Input';

export interface SignalsFilters {
  priority: string | null;
  status: string | null;
  riskScoreFrom: number | null;
  riskScoreTo: number | null;
}

interface SignalsFilterModalProps {
  isOpen: boolean;
  filters: SignalsFilters;
  onClose: () => void;
  onApply: (filters: SignalsFilters) => void;
  onClear: () => void;
}

const priorityOptions = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const statusOptions = [
  { label: 'New', value: 'new' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Not relevant', value: 'dismissed' },
];

export const SignalsFilterModal: React.FC<SignalsFilterModalProps> = ({
  isOpen,
  filters,
  onClose,
  onApply,
  onClear,
}) => {
  const [localFilters, setLocalFilters] = useState<SignalsFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters: SignalsFilters = {
      priority: null,
      status: null,
      riskScoreFrom: null,
      riskScoreTo: null,
    };
    setLocalFilters(clearedFilters);
    onClear();
    onClose();
  };

  const selectedPriority = priorityOptions.find((opt) => opt.value === localFilters.priority);
  const selectedStatus = statusOptions.find((opt) => opt.value === localFilters.status);

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      borderColor: 'transparent',
      backgroundColor: '#F5F7FC',
      boxShadow: 'none',
      '&:hover': {
        outline: 'none',
        borderColor: 'transparent',
      },
      cursor: 'pointer',
      borderRadius: '0.5rem',
      minHeight: '3rem',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: '#F5F7FC',
      minWidth: 'fit-content',
    }),
    menuList: (base: any) => ({
      ...base,
      backgroundColor: '#F5F7FC',
      borderRadius: '0.5rem',
    }),
    option: (base: any, { isSelected }: any) => ({
      ...base,
      backgroundColor: 'transparent',
      color: isSelected ? '#181A1E' : '#707070',
      '&:hover': {
        color: '#181A1E',
      },
      cursor: 'pointer',
      fontWeight: isSelected ? '600' : '400',
      fontSize: '0.875rem',
      borderRadius: '0.5rem',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      marginBottom: '2px',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#707070',
      fontSize: '0.875rem',
      paddingLeft: '2px',
    }),
    placeholder: (defaultStyles: any) => ({
      ...defaultStyles,
      color: '#707070',
      fontWeight: '400',
      fontSize: '0.875rem',
      paddingLeft: '2px',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#707070',
      paddingRight: '16px',
      '&:hover': {
        color: '#707070',
      },
    }),
    indicatorSeparator: () => ({}),
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-[32rem] max-w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-custom-black">Filter Signals</h3>
          <button onClick={onClose} className="text-custom-darkSilver hover:text-custom-black transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Priority</label>
            <Select
              value={selectedPriority || null}
              onChange={(option) => setLocalFilters({ ...localFilters, priority: option ? option.value : null })}
              options={priorityOptions}
              placeholder="Select priority"
              isClearable
              styles={customSelectStyles}
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Status</label>
            <Select
              value={selectedStatus || null}
              onChange={(option) => setLocalFilters({ ...localFilters, status: option ? option.value : null })}
              options={statusOptions}
              placeholder="Select status"
              isClearable
              styles={customSelectStyles}
            />
          </div>

          {/* Risk Score Filter */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Risk Score</label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="From"
                  value={localFilters.riskScoreFrom?.toString() || ''}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      riskScoreFrom: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                  maxLength={3}
                />
              </div>
              <span className="text-custom-darkSilver">-</span>
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="To"
                  value={localFilters.riskScoreTo?.toString() || ''}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      riskScoreTo: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                  maxLength={3}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="transparent" onClick={handleClear}>
            Clear
          </Button>
          <Button onClick={handleApply}>Apply Filters</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
